<template>
  <section class="mb-8">
    <h3
      class="font-roboto text-xl font-bold mb-2"
      id="required-cover-letter">
      Cover letter
    </h3>
    <p class="mb-4">
      Word or PDF
    </p>
    <UploadedFileList :fileListType="SubmissionFileType.CoverLetter" />
    <FormInputFile
      ref="fileInput"
      id="coverLetter"
      :multiple="false"
      :accept="[
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.doc',
        '.docx',
        'application/pdf',
        '.pdf',
        'application/x-latex',
        'text/plain',
        '.tex'
      ]"
      @custom-input="fileChanged"
      buttonDataTestId="upload-coverletter-file"
      :inline="true"
      :showSelectedFiles="false"
      class="mb-4"
      v-if="!!!coverLetter"
    >
    </FormInputFile>
    <InlineWarningMessage
      v-if="error ?? localError"
      :errorMessage="error ?? localError"
      errorTestId="cover-letter-upload"
    />
    <NotificationComponent
      v-if="hasError"
      data-testid="failed-to-upload-cover-letter"
      class="mb-8"
      role="alert"
      variant="error"
      heading-text="Upload error"
      :heading-level="1">
      <template #body>
        <UploadErrorMessage
          fileTypeLabel="cover letter"
          dataTestId="cover-letter"
          testId="CoverLetter"
        />
      </template>
    </NotificationComponent>
    <p class="mb-4">
      Your cover letter will be sent to the handling editor and NOT the reviewers. Please:
    </p>
    <ul class="list-disc pl-12 pr-4 mb-4">
      <li>
        do not include preferred/non-preferred reviewers
      </li>
      <li>
        do not include information about conflicts of interest
      </li>
    </ul>
    <p class="mb-4">
      If there is any information on conflicts of interest you would like to make us aware of email
      our editorial team at
      <a
        class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
        href="mailto:chemicalscience@rsc.org"
        id="lnk-supportingFileUpload-rscMail">
        chemicalscience@rsc.org</a>
      after submitting your manuscript.
    </p>
  </section>
</template>

<script setup lang="ts">
import { SubmissionFileType, SubmissionFile } from '@/types'
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useFileUpload } from '@/composables/useFileUpload'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import UploadedFileList from '../upload/UploadedFileList.vue'
import FormInputFile from '../common/FormInputFile.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import UploadErrorMessage from './UploadErrorMessage.vue'

const store = useStore()
const fileInput = ref<HTMLInputElement | null>(null)

const { uploadFile, localErrors, clearErrors } = useFileUpload()

const coverLetter = computed((): SubmissionFile => {
  return store.getters.submissionFiles.find((file: { type: SubmissionFileType }) => file.type === SubmissionFileType.CoverLetter) as SubmissionFile
})

const hasError = ref(false)

const localError = computed(() => {
  if (localErrors.value.length > 0) {
    return localErrors.value[0].text
  }
  return undefined
})

defineProps<{ error?: string }>()
const emit = defineEmits(['clear-error'])

const fileChanged = async (fileArray: FileList | File[] | null = null) => {
  if (!fileArray && !fileInput.value) return
  const inputFiles = fileArray as FileList | File[] | null
  if (!inputFiles || !inputFiles[0]) {
    return
  }

  clearErrors()
  hasError.value = false
  const file = inputFiles[0] as File

  await uploadFile(
    file,
    SubmissionFileType.CoverLetter,
    'coverletter',
    {
      successMessage: 'Your coverletter has been successfully added.',
      onSuccess: () => {
        emit('clear-error')
        hasError.value = false
      },
      onError: () => {
        hasError.value = true
      },
      onValidationError: (error, file) => {
        emit('clear-error')
        localErrors.value.push({
          fileName: file.name,
          text: error instanceof Error ? error.message : 'Validation error',
        })
      },
    },
  )
}
</script>
